import {
  frameChunks,
  frameDuration,
  loopCount,
  muxAnimatedWebP,
} from './animated_webp.js'

// Pictures are made smaller in the browser before they are uploaded: at
// most 2048 px on the long side, as WebP (or JPEG) at quality 0.85. GIFs
// become animated WebP where the browser can decode them frame by frame.
const MAX_SIZE = 2048
const QUALITY = 0.85
// A longer GIF goes up as it is: every frame is encoded one by one, and the
// encoded frames are held until the end.
const MAX_FRAMES = 1000
const MAX_PIXELS = 200_000_000

// an animated PNG has an acTL chunk
const isAnimatedPng = async (file) => {
  const view = new Uint8Array(await file.arrayBuffer())
  for (let i = 0; i < view.length - 3; i++) {
    if (
      view[i] === 0x61 &&
      view[i + 1] === 0x63 &&
      view[i + 2] === 0x54 &&
      view[i + 3] === 0x4c
    ) {
      return true
    }
  }
  return false
}

const supportsWebP = () =>
  document
    .createElement('canvas')
    .toDataURL('image/webp')
    .startsWith('data:image/webp')

// ImageDecoder is missing in Safari
const supportsGifToWebP = async () =>
  typeof ImageDecoder !== 'undefined' &&
  supportsWebP() &&
  (await ImageDecoder.isTypeSupported('image/gif'))

// Whether fileForUpload may change the file: still pictures, and GIFs where
// they can become animated WebP (not with jpegOnly). Not animated PNGs:
// they would lose their animation.
export const compressible = async (file, { jpegOnly = false } = {}) => {
  if (file.type === 'image/gif') {
    return !jpegOnly && (await supportsGifToWebP())
  }
  if (!file.type.startsWith('image/')) return false
  return !(file.type === 'image/png' && (await isAnimatedPng(file)))
}

// the size that fits in MAX_SIZE, keeping the proportions
const fitted = (width, height) => {
  if (width <= MAX_SIZE && height <= MAX_SIZE) return [width, height]
  return width > height
    ? [MAX_SIZE, Math.round((height * MAX_SIZE) / width)]
    : [Math.round((width * MAX_SIZE) / height), MAX_SIZE]
}

const renamed = (file, extension) =>
  file.name.replace(/\.[^/.]+$/, '') + extension

const encode = (canvas, type) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('cannot encode'))),
      type,
      QUALITY,
    ),
  )

// The picture smaller and re-encoded; call it for compressible files only.
// Rejects when the browser cannot read the picture (e.g. HEIC in Chrome).
export const compressImage = (file, { jpegOnly = false } = {}) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error(`cannot read ${file.name}`))
    }
    img.onload = () => {
      URL.revokeObjectURL(img.src)
      const [width, height] = fitted(img.width, img.height)
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d').drawImage(img, 0, 0, width, height)

      const type = !jpegOnly && supportsWebP() ? 'image/webp' : 'image/jpeg'
      const name = renamed(file, type === 'image/webp' ? '.webp' : '.jpg')

      encode(canvas, type).then(
        (blob) =>
          resolve(new File([blob], name, { type, lastModified: Date.now() })),
        () => reject(new Error(`cannot encode ${file.name}`)),
      )
    }
    img.src = URL.createObjectURL(file)
  })

const webpFile = (data, gif) =>
  new File([data], renamed(gif, '.webp'), {
    type: 'image/webp',
    lastModified: Date.now(),
  })

// A GIF as animated WebP (a still one for one frame), its frames scaled like
// pictures; null when it is over the frame or pixel budget. Call it where supportsGifToWebP; rejects
// when the GIF cannot be decoded.
export const gifToWebP = async (
  file,
  { maxFrames = MAX_FRAMES, maxPixels = MAX_PIXELS } = {},
) => {
  const decoder = new ImageDecoder({
    data: await file.arrayBuffer(),
    type: 'image/gif',
  })
  try {
    await decoder.tracks.ready
    // frameCount can still grow while the data is parsed
    await decoder.completed
    const track = decoder.tracks.selectedTrack
    const count = track.frameCount
    if (count > maxFrames) return null

    // ImageDecoder gives whole frames (disposal done), so each one is drawn
    // on a cleared canvas and encoded as a still WebP
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const frames = []
    for (let frameIndex = 0; frameIndex < count; frameIndex++) {
      const { image } = await decoder.decode({ frameIndex })
      let duration
      try {
        if (frameIndex === 0) {
          ;[canvas.width, canvas.height] = fitted(
            image.displayWidth,
            image.displayHeight,
          )
          if (count * canvas.width * canvas.height > maxPixels) return null
        }
        context.clearRect(0, 0, canvas.width, canvas.height)
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        duration = frameDuration(image.duration)
      } finally {
        image.close()
      }
      const still = await encode(canvas, 'image/webp')
      // one frame: a still WebP, so the server makes still previews of it
      if (count === 1) return webpFile(still, file)
      const { chunks, alpha } = frameChunks(
        new Uint8Array(await still.arrayBuffer()),
      )
      frames.push({ chunks, alpha, duration })
    }

    const webp = muxAnimatedWebP({
      width: canvas.width,
      height: canvas.height,
      loops: loopCount(track.repetitionCount),
      frames,
    })
    return webpFile(webp, file)
  } finally {
    decoder.close()
  }
}

// A GIF goes up as WebP only when that is smaller
const compressed = async (file, jpegOnly) => {
  if (file.type !== 'image/gif') return compressImage(file, { jpegOnly })
  const webp = await gifToWebP(file)
  return webp && webp.size < file.size ? webp : null
}

// The file to upload: compressed when asked and possible, else the
// original. compressed says which one it is.
export const fileForUpload = async (file, { compress, jpegOnly } = {}) => {
  if (!compress || !(await compressible(file, { jpegOnly }))) {
    return { file, compressed: false }
  }
  try {
    const smaller = await compressed(file, jpegOnly)
    return smaller
      ? { file: smaller, compressed: true }
      : { file, compressed: false }
  } catch {
    return { file, compressed: false }
  }
}
